import { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import Cropper from 'react-easy-crop';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  Image as ImageIcon, 
  Video, 
  Music, 
  X, 
  MapPin, 
  Loader2,
  Upload,
  CheckCircle,
  Crop,
  RotateCw,
  ZoomIn,
  ZoomOut,
  Check,
  Link as LinkIcon,
  FileVideo,
  Youtube,
  Play,
  AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_VIDEO_SIZE_MB = 50;

// Helper function to create cropped image
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

// Get cropped image as blob
async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  canvas.width = safeArea;
  canvas.height = safeArea;

  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-safeArea / 2, -safeArea / 2);

  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5
  );

  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.9);
  });
}

// Convert YouTube watch URL to embed URL
const convertYouTubeUrl = (url) => {
  if (!url) return null;
  
  // Already an embed URL
  if (url.includes('youtube.com/embed/')) {
    return url;
  }
  
  // Handle youtube.com/watch?v= format
  const watchMatch = url.match(/(?:youtube\.com\/watch\?v=|youtube\.com\/watch\?.*&v=)([a-zA-Z0-9_-]{11})/);
  if (watchMatch) {
    return `https://www.youtube.com/embed/${watchMatch[1]}`;
  }
  
  // Handle youtu.be/ short format
  const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
  if (shortMatch) {
    return `https://www.youtube.com/embed/${shortMatch[1]}`;
  }
  
  // Handle youtube.com/v/ format
  const vMatch = url.match(/youtube\.com\/v\/([a-zA-Z0-9_-]{11})/);
  if (vMatch) {
    return `https://www.youtube.com/embed/${vMatch[1]}`;
  }
  
  return null;
};

// Get YouTube video ID
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtube\.com\/watch\?.*&v=)([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  
  return null;
};

// Check if URL is a direct video file
const isDirectVideoUrl = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.m4v'];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some(ext => lowerUrl.includes(ext));
};

// Check if URL is a YouTube URL
const isYouTubeUrl = (url) => {
  if (!url) return false;
  return url.includes('youtube.com') || url.includes('youtu.be');
};

export default function CreatePostPage({ user }) {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const [formData, setFormData] = useState({
    content: '',
    post_type: 'text',
    media_url: '',
    media_type: '',
    mantra_meaning: '',
    location: null,
  });
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [locationStatus, setLocationStatus] = useState('idle');
  
  // Video input method
  const [videoInputMethod, setVideoInputMethod] = useState(null); // 'upload', 'youtube', 'url'
  const [videoUrlInput, setVideoUrlInput] = useState('');
  const [videoUrlType, setVideoUrlType] = useState(null); // 'youtube', 'direct'
  const [videoUrlValid, setVideoUrlValid] = useState(false);
  
  // Cropping state
  const [showCropper, setShowCropper] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [aspectRatio, setAspectRatio] = useState(1);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Check if we received camera media from CameraPage
  useEffect(() => {
    const cameraMedia = routeLocation.state?.cameraMedia;
    if (cameraMedia) {
      setFormData(prev => ({
        ...prev,
        media_url: cameraMedia.url,
        media_type: cameraMedia.type,
        post_type: cameraMedia.type,
        content: cameraMedia.caption || '',
      }));
      setPreviewUrl(cameraMedia.url);
    }
  }, [routeLocation.state]);

  // Handle image file selection
  const handleImageFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.error('Image too large. Maximum size is 20MB');
      return;
    }

    const imageUrl = URL.createObjectURL(file);
    setCropImageSrc(imageUrl);
    setShowCropper(true);
    setFormData(prev => ({ 
      ...prev, 
      post_type: 'image',
      media_type: 'image'
    }));
  };

  // Handle video file selection
  const handleVideoFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }

    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_VIDEO_SIZE_MB) {
      toast.error(`Video too large. Maximum size is ${MAX_VIDEO_SIZE_MB}MB (yours: ${sizeMB.toFixed(1)}MB)`);
      return;
    }

    // Validate video duration
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      if (video.duration > 120) {
        toast.error('Video must be 2 minutes or less');
        return;
      }
      
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setFormData(prev => ({ 
        ...prev, 
        post_type: 'video',
        media_type: 'video'
      }));
      setVideoInputMethod('upload');
      toast.success(`Video selected (${sizeMB.toFixed(1)}MB)`);
    };
    video.onerror = () => {
      toast.error('Could not load video. Please try a different file.');
    };
    video.src = URL.createObjectURL(file);
  };

  // Handle video URL input change
  const handleVideoUrlChange = (url) => {
    setVideoUrlInput(url);
    
    if (!url.trim()) {
      setVideoUrlType(null);
      setVideoUrlValid(false);
      return;
    }

    if (isYouTubeUrl(url)) {
      const embedUrl = convertYouTubeUrl(url);
      if (embedUrl) {
        setVideoUrlType('youtube');
        setVideoUrlValid(true);
        setFormData(prev => ({
          ...prev,
          post_type: 'video',
          media_type: 'youtube',
          media_url: embedUrl
        }));
      } else {
        setVideoUrlType('youtube');
        setVideoUrlValid(false);
      }
    } else if (isDirectVideoUrl(url)) {
      setVideoUrlType('direct');
      setVideoUrlValid(true);
      setFormData(prev => ({
        ...prev,
        post_type: 'video',
        media_type: 'video',
        media_url: url
      }));
    } else {
      // Try as direct URL anyway
      setVideoUrlType('direct');
      setVideoUrlValid(true);
      setFormData(prev => ({
        ...prev,
        post_type: 'video',
        media_type: 'video',
        media_url: url
      }));
    }
  };

  // Apply video URL
  const applyVideoUrl = () => {
    if (!videoUrlValid) {
      toast.error('Please enter a valid video URL');
      return;
    }
    
    setVideoInputMethod(videoUrlType === 'youtube' ? 'youtube' : 'url');
    setPreviewUrl(videoUrlInput);
    toast.success(videoUrlType === 'youtube' ? 'YouTube video added!' : 'Video URL added!');
  };

  // Apply crop and create final image
  const applyCrop = async () => {
    try {
      const croppedBlob = await getCroppedImg(cropImageSrc, croppedAreaPixels, rotation);
      const croppedFile = new File([croppedBlob], 'cropped_image.jpg', { type: 'image/jpeg' });
      
      setSelectedFile(croppedFile);
      setPreviewUrl(URL.createObjectURL(croppedBlob));
      setShowCropper(false);
      setCropImageSrc(null);
      setRotation(0);
      setZoom(1);
      toast.success('Image cropped successfully!');
    } catch (error) {
      console.error('Crop error:', error);
      toast.error('Failed to crop image');
    }
  };

  // Cancel cropping
  const cancelCrop = () => {
    setShowCropper(false);
    setCropImageSrc(null);
    setRotation(0);
    setZoom(1);
    setFormData(prev => ({ ...prev, post_type: 'text', media_type: '' }));
  };

  // Skip cropping and use original
  const skipCrop = async () => {
    const response = await fetch(cropImageSrc);
    const blob = await response.blob();
    const file = new File([blob], 'image.jpg', { type: blob.type });
    
    setSelectedFile(file);
    setPreviewUrl(cropImageSrc);
    setShowCropper(false);
    setCropImageSrc(null);
  };

  // Upload media file to server
  const uploadMedia = async () => {
    if (!selectedFile) return null;

    setUploading(true);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('dharmbook_token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', selectedFile);

      const response = await axios.post(`${API}/media/upload`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      return response.data;
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload media');
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Request location permission
  const requestLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Location not supported by your browser');
      return;
    }

    setLocationStatus('loading');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );
          const data = await response.json();
          
          setFormData(prev => ({
            ...prev,
            location: {
              city: data.address?.city || data.address?.town || data.address?.village || '',
              region: data.address?.state || data.address?.region || '',
              lat: latitude,
              lng: longitude
            }
          }));
          setLocationStatus('granted');
          toast.success('Location added');
        } catch {
          setFormData(prev => ({
            ...prev,
            location: { lat: latitude, lng: longitude }
          }));
          setLocationStatus('granted');
        }
      },
      (error) => {
        console.error('Location error:', error);
        setLocationStatus('denied');
        toast.error('Location access denied');
      },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  const removeLocation = () => {
    setFormData(prev => ({ ...prev, location: null }));
    setLocationStatus('idle');
  };

  const removeMedia = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setVideoInputMethod(null);
    setVideoUrlInput('');
    setVideoUrlType(null);
    setVideoUrlValid(false);
    setFormData(prev => ({ 
      ...prev, 
      media_url: '', 
      media_type: '',
      post_type: 'text'
    }));
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  // Submit post
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.content.trim() && !selectedFile && !formData.media_url) {
      toast.error('Please add some content or media');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('dharmbook_token');
      
      let mediaData = {};
      
      // Handle file upload
      if (selectedFile) {
        const uploadResult = await uploadMedia();
        if (uploadResult) {
          mediaData = {
            media_url: uploadResult.media_url,
            media_type: uploadResult.media_type
          };
        } else {
          setLoading(false);
          return;
        }
      } 
      // Handle URL-based media
      else if (formData.media_url) {
        mediaData = {
          media_url: formData.media_url,
          media_type: formData.media_type
        };
      }

      await axios.post(`${API}/posts`, {
        ...formData,
        ...mediaData,
        content: formData.content || (selectedFile || formData.media_url ? '📷' : '')
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Post created successfully! 🙏');
      navigate('/');
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  // Get YouTube thumbnail
  const getYouTubeThumbnail = (url) => {
    const videoId = getYouTubeVideoId(url);
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    }
    return null;
  };

  // Image Cropper Modal
  if (showCropper && cropImageSrc) {
    return (
      <div className="fixed inset-0 bg-black z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-gray-900">
          <button
            onClick={cancelCrop}
            className="text-white p-2 hover:bg-gray-800 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
          <h2 className="text-white font-semibold text-lg">Crop Image</h2>
          <button
            onClick={applyCrop}
            className="bg-[#FF9933] text-white px-4 py-2 rounded-full font-medium flex items-center gap-2"
            data-testid="apply-crop-btn"
          >
            <Check className="w-5 h-5" />
            Done
          </button>
        </div>

        {/* Cropper */}
        <div className="flex-1 relative">
          <Cropper
            image={cropImageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspectRatio}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            style={{
              containerStyle: { background: '#000' }
            }}
          />
        </div>

        {/* Controls */}
        <div className="bg-gray-900 p-4 space-y-4">
          {/* Aspect Ratio */}
          <div className="flex items-center justify-center gap-2">
            <span className="text-gray-400 text-sm mr-2">Aspect:</span>
            {[
              { label: '1:1', value: 1 },
              { label: '4:3', value: 4/3 },
              { label: '16:9', value: 16/9 },
              { label: 'Free', value: undefined },
            ].map((ratio) => (
              <button
                key={ratio.label}
                onClick={() => setAspectRatio(ratio.value)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  aspectRatio === ratio.value 
                    ? 'bg-[#FF9933] text-white' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {ratio.label}
              </button>
            ))}
          </div>

          {/* Zoom */}
          <div className="flex items-center gap-3">
            <ZoomOut className="w-5 h-5 text-gray-400" />
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#FF9933]"
            />
            <ZoomIn className="w-5 h-5 text-gray-400" />
          </div>

          {/* Rotation */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => setRotation((r) => r - 90)}
              className="p-2 bg-gray-700 rounded-full hover:bg-gray-600 transition-colors"
            >
              <RotateCw className="w-5 h-5 text-white transform -scale-x-100" />
            </button>
            <span className="text-gray-400 text-sm">{rotation}°</span>
            <button
              onClick={() => setRotation((r) => r + 90)}
              className="p-2 bg-gray-700 rounded-full hover:bg-gray-600 transition-colors"
            >
              <RotateCw className="w-5 h-5 text-white" />
            </button>
          </div>

          <button
            onClick={skipCrop}
            className="w-full py-2 text-gray-400 hover:text-white transition-colors text-sm"
          >
            Skip cropping, use original
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto px-2 sm:px-4 py-4 sm:py-6 pb-20 md:pb-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden w-full">
        {/* Header */}
        <div className="border-b border-gray-200 p-3 sm:p-4 flex items-center justify-between">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900" style={{ fontFamily: 'Merriweather, serif' }}>
            Create Post
          </h2>
          <button 
            onClick={() => navigate('/')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="close-create-post"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* User Info */}
        <div className="p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
          <img 
            src={user?.profile_photo_url || user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=FF9933&color=fff`} 
            alt={user?.name} 
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover flex-shrink-0"
          />
          <div>
            <h3 className="font-semibold text-gray-900">{user?.name}</h3>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              {formData.location && (
                <span className="flex items-center gap-1 text-green-600">
                  <MapPin className="w-3 h-3" />
                  {formData.location.city || 'Location added'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Content Input */}
        <form onSubmit={handleSubmit}>
          <div className="px-4">
            <Textarea
              data-testid="content-input"
              placeholder={`What's on your mind, ${user?.name?.split(' ')[0]}?`}
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={4}
              className="border-0 focus:ring-0 resize-none text-lg placeholder:text-gray-400"
            />
          </div>

          {/* Mantra Meaning */}
          {formData.post_type === 'mantra' && (
            <div className="px-4 py-2">
              <Input
                data-testid="mantra-meaning-input"
                placeholder="Meaning of the mantra (optional)"
                value={formData.mantra_meaning}
                onChange={(e) => setFormData({ ...formData, mantra_meaning: e.target.value })}
                className="rounded-xl border-gray-200"
              />
            </div>
          )}

          {/* Media Preview */}
          {(previewUrl || formData.media_url) && (
            <div className="px-4 py-2 relative">
              {/* YouTube Embed Preview */}
              {videoInputMethod === 'youtube' && formData.media_url && (
                <div className="relative rounded-lg overflow-hidden bg-black aspect-video">
                  <iframe
                    src={formData.media_url}
                    title="YouTube video preview"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              )}
              
              {/* Direct Video URL Preview */}
              {videoInputMethod === 'url' && formData.media_url && (
                <video 
                  src={formData.media_url} 
                  controls 
                  className="w-full max-h-80 object-contain rounded-lg bg-black"
                />
              )}
              
              {/* Uploaded Video Preview */}
              {videoInputMethod === 'upload' && previewUrl && formData.media_type === 'video' && (
                <video 
                  src={previewUrl} 
                  controls 
                  className="w-full max-h-80 object-contain rounded-lg bg-black"
                />
              )}
              
              {/* Image Preview */}
              {formData.media_type === 'image' && previewUrl && (
                <div className="relative">
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    className="w-full max-h-80 object-contain rounded-lg bg-gray-100"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setCropImageSrc(previewUrl);
                      setShowCropper(true);
                    }}
                    className="absolute bottom-3 left-3 px-3 py-1.5 bg-black/60 hover:bg-black/80 text-white text-sm rounded-lg flex items-center gap-1.5 transition-colors"
                  >
                    <Crop className="w-4 h-4" />
                    Crop
                  </button>
                </div>
              )}
              
              {/* Remove Button */}
              <button
                type="button"
                onClick={removeMedia}
                className="absolute top-4 right-6 p-2 bg-gray-900/70 rounded-full hover:bg-gray-900 transition-colors"
              >
                <X className="w-4 h-4 text-white" />
              </button>
              
              {/* Video Type Badge */}
              {videoInputMethod && (
                <div className="absolute top-4 left-6 px-2 py-1 bg-black/70 rounded-lg flex items-center gap-1.5">
                  {videoInputMethod === 'youtube' && <Youtube className="w-4 h-4 text-red-500" />}
                  {videoInputMethod === 'url' && <LinkIcon className="w-4 h-4 text-blue-400" />}
                  {videoInputMethod === 'upload' && <FileVideo className="w-4 h-4 text-green-400" />}
                  <span className="text-white text-xs capitalize">{videoInputMethod}</span>
                </div>
              )}
              
              {/* Upload Progress */}
              {uploading && (
                <div className="absolute inset-0 bg-black/50 rounded-lg flex flex-col items-center justify-center">
                  <Loader2 className="w-8 h-8 text-white animate-spin mb-2" />
                  <div className="text-white text-sm">{uploadProgress}%</div>
                  <div className="w-32 h-1.5 bg-gray-600 rounded-full mt-2">
                    <div 
                      className="h-full bg-[#FF9933] rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Video URL Input Section */}
          {formData.post_type === 'video' && !videoInputMethod && !previewUrl && (
            <div className="px-4 py-3 bg-gray-50 mx-4 rounded-xl space-y-3">
              <p className="text-sm font-medium text-gray-700">Add Video</p>
              
              {/* Input Field */}
              <div className="relative">
                <Input
                  data-testid="video-url-input"
                  placeholder="Paste YouTube link or video URL (mp4)"
                  value={videoUrlInput}
                  onChange={(e) => handleVideoUrlChange(e.target.value)}
                  className="rounded-xl border-gray-200 pr-24"
                />
                {videoUrlInput && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                    {videoUrlValid ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-orange-500" />
                    )}
                    {videoUrlType === 'youtube' && <Youtube className="w-5 h-5 text-red-500" />}
                  </div>
                )}
              </div>
              
              {/* Preview Thumbnail for YouTube */}
              {videoUrlType === 'youtube' && videoUrlValid && (
                <div className="relative rounded-lg overflow-hidden bg-gray-100">
                  <img 
                    src={getYouTubeThumbnail(videoUrlInput)}
                    alt="YouTube Thumbnail"
                    className="w-full h-40 object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
                      <Play className="w-8 h-8 text-white ml-1" fill="white" />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Apply Button */}
              {videoUrlInput && (
                <Button
                  type="button"
                  onClick={applyVideoUrl}
                  disabled={!videoUrlValid}
                  className="w-full rounded-xl bg-[#FF9933] hover:bg-[#E68A00] disabled:bg-gray-300"
                >
                  {videoUrlType === 'youtube' ? 'Add YouTube Video' : 'Add Video URL'}
                </Button>
              )}
              
              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400">or</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              
              {/* Upload from Device */}
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                onChange={handleVideoFileSelect}
                className="hidden"
                data-testid="video-file-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => videoInputRef.current?.click()}
                className="w-full rounded-xl border-gray-200 text-gray-700"
              >
                <FileVideo className="w-5 h-5 mr-2 text-green-500" />
                Upload from Device (Max {MAX_VIDEO_SIZE_MB}MB)
              </Button>
              
              {/* Cancel */}
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, post_type: 'text' }))}
                className="w-full text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Location Badge */}
          {formData.location && (
            <div className="px-4 py-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm">
                <MapPin className="w-4 h-4" />
                <span>
                  {formData.location.city && formData.location.region 
                    ? `${formData.location.city}, ${formData.location.region}`
                    : 'Location added'}
                </span>
                <button type="button" onClick={removeLocation} className="hover:text-red-500">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Add to Post Options */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Add to your post</span>
              <div className="flex items-center gap-1">
                {/* Image Picker */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageFileSelect}
                  className="hidden"
                  data-testid="image-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!!videoInputMethod || formData.post_type === 'video'}
                  className={`p-2.5 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-40 ${
                    formData.media_type === 'image' ? 'bg-green-50 text-green-600' : ''
                  }`}
                  title="Add photo"
                  data-testid="add-image-btn"
                >
                  <ImageIcon className="w-6 h-6 text-green-500" />
                </button>

                {/* Video */}
                <button
                  type="button"
                  onClick={() => {
                    if (formData.post_type === 'video') {
                      removeMedia();
                    } else {
                      setFormData(prev => ({ ...prev, post_type: 'video' }));
                    }
                  }}
                  disabled={formData.media_type === 'image'}
                  className={`p-2.5 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-40 ${
                    formData.post_type === 'video' ? 'bg-red-50' : ''
                  }`}
                  title="Add video"
                  data-testid="add-video-btn"
                >
                  <Video className="w-6 h-6 text-red-500" />
                </button>

                {/* Audio */}
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ 
                    ...prev, 
                    post_type: prev.post_type === 'audio' ? 'text' : 'audio' 
                  }))}
                  disabled={!!videoInputMethod || formData.media_type === 'image'}
                  className={`p-2.5 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-40 ${
                    formData.post_type === 'audio' ? 'bg-purple-50' : ''
                  }`}
                  title="Add audio"
                >
                  <Music className="w-6 h-6 text-purple-500" />
                </button>

                {/* Location */}
                <button
                  type="button"
                  onClick={formData.location ? removeLocation : requestLocation}
                  disabled={locationStatus === 'loading'}
                  className={`p-2.5 rounded-full hover:bg-gray-100 transition-colors ${
                    formData.location ? 'bg-blue-50 text-blue-600' : ''
                  }`}
                  title="Add location"
                  data-testid="add-location-btn"
                >
                  {locationStatus === 'loading' ? (
                    <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                  ) : (
                    <MapPin className={`w-6 h-6 ${formData.location ? 'text-blue-600' : 'text-blue-500'}`} />
                  )}
                </button>
              </div>
            </div>

            {/* Audio URL Input */}
            {formData.post_type === 'audio' && (
              <div className="mt-3">
                <Input
                  data-testid="audio-url-input"
                  placeholder="Paste audio URL"
                  value={formData.media_url}
                  onChange={(e) => setFormData({ ...formData, media_url: e.target.value, media_type: 'audio' })}
                  className="rounded-xl border-gray-200"
                />
              </div>
            )}
          </div>

          {/* Privacy Notice */}
          <div className="px-4 pb-3">
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <CheckCircle className="w-3 h-3 text-green-500" />
              DharmBook respects your privacy. Media and location are shared only with your permission.
            </p>
          </div>

          {/* Submit Button */}
          <div className="px-4 pb-4">
            <Button
              type="submit"
              data-testid="create-post-btn"
              disabled={loading || uploading || (!formData.content.trim() && !selectedFile && !formData.media_url)}
              className="w-full rounded-full bg-[#FF9933] hover:bg-[#E68A00] disabled:bg-gray-300 text-white font-semibold py-5 shadow-md hover:-translate-y-0.5 transition-all"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating...
                </span>
              ) : uploading ? (
                <span className="flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Uploading {uploadProgress}%
                </span>
              ) : (
                'Post'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
